/* MolCellQuest — 風景の手続き的なパーツ
   SVG 文字列ではなく「データ」を返す。Svelte 側で {#each} すれば
   ノードが使い回され、CSS トランジションがそのまま効く。 */

/** 0〜1 の疑似乱数。同じ seed なら常に同じ値（GLSL 風のハッシュ）。 */
function hash(n) {
  const v = Math.sin(n) * 43758.5453;
  return v - Math.floor(v);
}

/**
 * 決定論的な星空。再描画しても位置がちらつかない。
 * @returns {{x:number,y:number,r:number,op:number,delay:number}[]}
 */
export function journeyStars(n, seed = 0, w = 320, h = 140) {
  const out = [];
  for (let k = 0; k < n; k++) {
    const i = k + 1 + seed;
    const t = hash(i * 39.425);
    out.push({
      x: +(hash(i * 12.9898) * w).toFixed(1),
      y: +(hash(i * 78.233) * h).toFixed(1),
      r: +(0.4 + t * 1.3).toFixed(2),
      op: +(0.3 + t * 0.7).toFixed(2),
      delay: +(t * 3).toFixed(2),
    });
  }
  return out;
}

/**
 * 道に沿った通過跡。path の接線から向きを取り、左右交互にずらす。
 * @param {SVGPathElement} path
 * @param {number} frac 0〜1
 * @param {"foot"|"bubble"|"dust"|"none"} kind
 */
export function trailMarks(path, frac, kind) {
  if (!path || frac <= 0 || kind === "none") return [];
  let total;
  try {
    total = path.getTotalLength();
  } catch (e) {
    return [];
  }
  if (!total) return [];

  const done = total * Math.min(1, frac);
  const step = 13;
  const maxMarks = 90; // 道が長くても要素が増えすぎないように上限を設ける
  const stride = Math.max(step, done / maxMarks);

  const out = [];
  let left = true;
  for (let d = stride * 0.6; d < done - 2; d += stride) {
    const p = path.getPointAtLength(d);
    const p2 = path.getPointAtLength(Math.min(total, d + 0.8));
    const ang = Math.atan2(p2.y - p.y, p2.x - p.x);
    // 古い跡ほど薄く
    const fade = Math.max(0.16, Math.min(0.55, (d / done) * 0.55));

    if (kind === "foot") {
      const nx = Math.cos(ang + Math.PI / 2);
      const ny = Math.sin(ang + Math.PI / 2);
      const off = left ? 2.1 : -2.1;
      left = !left;
      out.push({
        kind: "foot",
        x: +(p.x + nx * off).toFixed(1),
        y: +(p.y + ny * off).toFixed(1),
        rot: +((ang * 180) / Math.PI).toFixed(0),
        op: +fade.toFixed(2),
      });
    } else if (kind === "bubble") {
      out.push({
        kind: "bubble",
        x: +p.x.toFixed(1),
        y: +(p.y - 2).toFixed(1),
        r: +(1.2 + fade * 1.6).toFixed(1),
        op: +(fade * 0.8).toFixed(2),
      });
    } else {
      out.push({
        kind: "dust",
        x: +p.x.toFixed(1),
        y: +p.y.toFixed(1),
        r: +(1.1 + fade).toFixed(1),
        op: +(fade * 0.7).toFixed(2),
      });
    }
  }
  return out;
}

/**
 * 道の上の位置（0〜1）を viewBox 座標に変換する。
 * チェックポイントの道標と旅人の配置に使う。
 */
export function pointsAlong(path, positions) {
  if (!path) return positions.map(() => ({ x: 0, y: 0 }));
  let total;
  try {
    total = path.getTotalLength();
  } catch (e) {
    return positions.map(() => ({ x: 0, y: 0 }));
  }
  return positions.map((t) => {
    const p = path.getPointAtLength(total * Math.max(0, Math.min(1, t)));
    return { x: +p.x.toFixed(1), y: +p.y.toFixed(1) };
  });
}
